package dev.lingvo.site.components.studio.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import dev.lingvo.site.i18n.trf

/** A scrollable full-language list makes the longest or overflowing caption directly reachable. */
@Composable
internal fun LanguageRail(state: StudioUiState, dispatch: (StudioIntent) -> Unit) {
    var longestFirst by remember { mutableStateOf(false) }
    val palette = ColorMode.current.toSitePalette()
    Div(attrs = { attr("data-studio-region", "rail"); tabIndex(-1); attr("aria-label", tr("studio.editor.language_rail.languages", "Languages"))
        style { property("display", "flex"); property("flex-direction", "column"); property("min-width", "0"); property("min-height", "0"); property("gap", Space.XS.toString()) } }) {
        StudioButton(if (longestFirst) t("studio.editor.language_rail.project_order", "Project order") else t("studio.editor.language_rail.longest_caption_first", "Longest caption first")) {
            longestFirst = !longestFirst
        }
        Div(attrs = { attr("data-studio-language-list", ""); style { property("overflow-y", "auto"); property("min-height", "0"); property("flex", "1") } }) {
            (if (longestFirst) state.languages.sortedByDescending { it.captionLength } else state.languages).forEach { language ->
                val fit = if (language.overflow) t("studio.editor.language_rail.does_not_fit", "Does not fit") else language.fittedSize?.let {
                    tf("studio.fit_pixels", "Fits at {size} px", "size" to it)
                }.orEmpty()
                val status = listOf(language.status, fit).filter { it.isNotEmpty() }.joinToString(", ")
                Button(attrs = {
                    attr("aria-label", trf("studio.language_status", "{language}, {code}{status}", "language" to language.label,
                        "code" to language.locale, "status" to status.takeIf { it.isNotEmpty() }?.let { ", $it" }.orEmpty()))
                    attr("aria-current", (language.locale == state.view.locale).toString())
                    style {
                        property("display", "grid"); property("gap", Space.XS.toString()); property("padding", Space.SM.toString())
                        property("min-height", "40px"); property("width", "100%"); property("font", "inherit"); property("text-align", "start")
                        property("background", palette.surface.toString()); property("color", palette.textPrimary.toString())
                        property("border-radius", Radius.Control.toString())
                        property("border", "1px solid ${if (language.locale == state.view.locale) palette.accent else palette.borderStrong}")
                    }
                    onClick { dispatch(StudioIntent.Language(language.locale)) }
                }) {
                    StudioNameStatus(language.label, language.locale)
                    if (status.isNotEmpty()) StudioMetaText(" $status")
                }
            }
        }
    }
}
